<script lang="ts">
    import { _ } from 'svelte-i18n';
    import { onMount } from 'svelte';
    import { get } from 'svelte/store';
    import { openModal } from 'svelte-modals';

    import ModalInfo from '../../shared/Modals/ModalInfo.svelte';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import MessageNotFound from '../../shared/Messages/MessageNotFound.svelte';
    import Switch from '../../shared/Switch.svelte';
    import { api } from '../../../stores/api';
    import UserPermissionRow from './UserPermissionRow.svelte';
    import {
        permissionErrorKey,
        UserPermissionsApi,
        visiblePanelUsers,
        type PanelUserType,
        type PermissionStateId,
    } from './UserPermissionsApi';
    import { debug } from '../../../stores/store';

    export let query: string = '';

    let users: PanelUserType[] = [];
    let loading: boolean = true;
    let error: string = '';
    /**
     * Konta bez dostępu (can_read = false) są domyślnie ukryte - to zwykle osoby, które odeszły
     * z firmy, a ich wiersze zostają w bazie dla historii uprawnień.
     */
    let showWithoutAccess: boolean = false;

    // filtrowanie po stronie frontu - lista jest pobierana raz, ani wyszukiwarka, ani przełącznik
    // nie generują żądań do API
    $: filteredUsers = visiblePanelUsers(users, query, showWithoutAccess);

    onMount(() => {
        void reload();
    });

    /**
     * Dołożenie wiersza zwróconego przez createUser() (modal dodawania otwierany ze strony).
     * Nowy użytkownik ląduje na górze, żeby był widoczny bez przewijania, i bez ponownego GET-a -
     * odpowiedź backendu ma dokładnie ten sam kształt co wiersz z listy.
     */
    export function addUser(user: PanelUserType): void {
        users = [user, ...users.filter(row => row.webuser_id !== user.webuser_id)];
    }

    async function reload(): Promise<void> {
        loading = true;
        try {
            users = await UserPermissionsApi.getUsers(get(api));
            error = '';
        } catch (e) {
            error = $_(permissionErrorKey(e));
        } finally {
            loading = false;
        }
    }

    async function changeState(user: PanelUserType, state: PermissionStateId): Promise<boolean> {
        try {
            const updated = await UserPermissionsApi.setPermissions(get(api), user.webuser_id, state);
            users = users.map(row => (row.webuser_id === updated.webuser_id ? updated : row));
            return true;
        } catch (e) {
            openModal(ModalInfo, { text: $_(permissionErrorKey(e)) });
            return false;
        }
    }
</script>

{#if loading}
    <Spinner />
{:else if error}
    <MessageDanger {error} />
{:else if users.length === 0}
    <MessageNotFound text={$_('No users found')} />
{:else}
    <div class="toolbar">
        <Switch bind:checked={showWithoutAccess} label={$_('Show users without access')} />
    </div>
    <div class="list">
        <div class="header">
            <div>{$_('User')}</div>
            <div>{$_('Role')}</div>
            <div>{$_('Permissions')}</div>
        </div>
        {#each filteredUsers as user (user.webuser_id)}
            <UserPermissionRow {user} onChange={changeState} />
        {:else}
            <div class="empty">
                <MessageNotFound text={$_('No users found')} />
            </div>
        {/each}
    </div>
{/if}

<style>
    .toolbar {
        display: flex;
        justify-content: flex-end;
        padding: 0 10px 8px;
    }
    .list {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        flex: 1;
    }
    .header {
        display: grid;
        grid-template-columns: minmax(200px, 1fr) 140px 200px;
        column-gap: 10px;
        padding: 8px 10px;
        border-bottom: 2px solid #f2f2f2;
        font-weight: 600;
        /*color: #555;*/
    }
    .empty {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 30px;
    }
</style>
